import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  Form,
  FormItem,
  FormControl,
  FormMessage,
  FormLabel,
  FormField,
} from "@repo/ui/components/ui/form";
import { FloatingLabelInput } from "@repo/ui/components/ui/floating-label-input";
import { FloatingLabelSelect } from "@repo/ui/components/ui/floating-label-select";
import { FloatingLabelTextarea } from "@repo/ui/components/ui/floating-label-textarea";
import { Checkbox } from "@repo/ui/components/ui/checkbox";
import { Button } from "@repo/ui/components/ui/button";
import { useState, useEffect } from "react";
import { useTranslations } from "next-intl";
import { cn } from "@repo/ui/lib/utils";
import "@repo/ui/globals.css";
import { useSearchParams } from "next/navigation";
import Link from "next/link";

const ContactForm = ({
  onSubmit,
  selectItems,
  className,
}: {
  onSubmit: (data: any) => void;
  selectItems: { value: string; label: string }[];
  className?: string;
}) => {
  const t = useTranslations("SharedHelp.Contact-form");
  const params = useSearchParams();

  const submitLabel = t("submit");

  const contactFormSchema = z
    .object({
      firstName: z.string().min(2, {
        message: "SharedHelp.Contact-form.empty-error",
      }),
      lastName: z.string().min(2, {
        message: "SharedHelp.Contact-form.empty-error",
      }),
      email: z
        .string()
        .min(2, { message: "SharedHelp.Contact-form.empty-error" })
        .email("SharedHelp.Contact-form.email-error"),
      topic: z.string().min(2, {
        message: "SharedHelp.Contact-form.empty-error",
      }),
      message: z.string().min(2, {
        message: "SharedHelp.Contact-form.empty-error",
      }),
      acceptConditions: z.boolean().refine((value) => value === true, {
        message: "SharedHelp.Contact-form.accept-conditions-error",
      }),
    })
    .required();

  function getDefaultsFromParams(key: string) {
    if (
      !params.has(key) ||
      (key === "topic" &&
        !selectItems
          .map((i) => i.value)
          .includes(params.get("topic") as string))
    ) {
      return "";
    }

    return params.get(key) as string;
  }

  const defaultValues = {
    firstName: getDefaultsFromParams("firstName"),
    lastName: getDefaultsFromParams("lastName"),
    email: getDefaultsFromParams("email"),
    topic: getDefaultsFromParams("topic"),
    message: getDefaultsFromParams("message"),
    acceptConditions: false,
  };

  const form = useForm({
    resolver: zodResolver(contactFormSchema),
    mode: "onTouched",
    defaultValues: {
      firstName: getDefaultsFromParams("firstName"),
      lastName: getDefaultsFromParams("lastName"),
      email: getDefaultsFromParams("email"),
      topic: getDefaultsFromParams("topic"),
      message: getDefaultsFromParams("message"),
      acceptConditions: false,
    },
  });

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = form;

  useEffect(() => {
    reset(defaultValues);
  }, [params]);

  const [count, setCount] = useState(0);

  const submit = (data: any) => {
    onSubmit(data);
    reset(defaultValues);
  };

  return (
    <div className={cn("w-full", className)}>
      <Form {...form}>
        <form
          name="contact-form"
          className="grid grid-cols-2 max-md:grid-cols-1 gap-5"
          onSubmit={handleSubmit(submit)}
        >
          <FormField
            name="firstName"
            control={control}
            render={({ field }) => (
              <FormItem>
                <FormControl>
                  <FloatingLabelInput label={t("name")} {...field} />
                </FormControl>
                <FormMessage>
                  {errors.firstName?.message?.toString()}
                </FormMessage>
              </FormItem>
            )}
          ></FormField>

          <FormField
            name="lastName"
            control={control}
            render={({ field }) => (
              <FormItem>
                <FormControl>
                  <FloatingLabelInput label={t("surname")} {...field} />
                </FormControl>
                <FormMessage>
                  {errors.lastName?.message?.toString()}
                </FormMessage>
              </FormItem>
            )}
          ></FormField>

          <FormField
            name="email"
            control={control}
            render={({ field }) => (
              <FormItem>
                <FormControl>
                  <FloatingLabelInput
                    type="email"
                    label={t("email")}
                    {...field}
                  />
                </FormControl>
                <FormMessage>{errors.email?.message?.toString()}</FormMessage>
              </FormItem>
            )}
          />

          <FormField
            name="topic"
            control={control}
            render={({ field }) => (
              <FormItem>
                <FormControl>
                  <FloatingLabelSelect
                    label={t("topic")}
                    {...field}
                    onChange={field.onChange}
                    value={field.value}
                    options={selectItems.map((item) => item.value)}
                    optionsLabels={selectItems.reduce(
                      (acc, item) => ({ ...acc, [item.value]: item.label }),
                      {} as { [key: string]: string },
                    )}
                    error={errors.topic}
                  />
                </FormControl>
                <FormMessage>{errors.topic?.message?.toString()}</FormMessage>
              </FormItem>
            )}
          />

          <FormField
            control={control}
            name="message"
            render={({ field }) => (
              <FormItem className="col-span-2 max-md:col-span-1">
                <FormControl>
                  <FloatingLabelTextarea
                    maxLength={2000}
                    label={t("message")}
                    {...field}
                    onChange={(e) => {
                      field.onChange(e);
                      setCount(e.target.value.length);
                    }}
                    classes="resize-none text-[12px]"
                  />
                </FormControl>
                <FormMessage>{errors.message?.message?.toString()}</FormMessage>
              </FormItem>
            )}
          />
          <span className="text-sm col-span-2 max-md:col-span-1 text-right max-md:text-left max-md:ml-6 max-md:text-[10px]">
            {`${count}/2000 ${t("message-chars")}`}
          </span>

          <FormField
            control={form.control}
            name="acceptConditions"
            render={({ field }) => (
              <FormItem className="flex flex-col col-span-2 max-md:col-span-1">
                <div className="flex items-center">
                  <FormControl>
                    <Checkbox
                      onCheckedChange={field.onChange}
                      checked={field.value}
                    />
                  </FormControl>
                  <FormLabel>
                    {t.rich("accept-conditions", {
                      a: (chunks) => (
                        <Link
                          href="/privacy"
                          target="_blank"
                          className="underline cursor-pointer hover:text-primary-magenta"
                        >
                          {chunks}
                        </Link>
                      ),
                    })}
                  </FormLabel>
                </div>
                <FormMessage>
                  {errors.acceptConditions?.message?.toString()}
                </FormMessage>
              </FormItem>
            )}
          />

          <div className="flex justify-center col-span-2 max-md:col-span-1">
            <Button className="w-80" type="submit">
              {submitLabel}
            </Button>
          </div>
        </form>
      </Form>
    </div>
  );
};

export default ContactForm;
